import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Box,
  IconButton,
  Typography,
  alpha
} from "@mui/material";
import { Add as AddIcon, RemoveCircleOutline as RemoveCircleOutlineIcon, Close, Edit as EditIcon, Email as EmailIcon, Phone as PhoneIcon } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";

const SimpleArrayField = ({ label, fieldName, formik, icon: IconComponent, type = "text" }) => {
  const [open, setOpen] = useState(false);
  const items = formik.values[fieldName] || [];
  const primary = items[0];
  const extraCount = items.filter(v => v.trim()).length - (primary?.trim() ? 1 : 0);
  const hasError = formik.touched[fieldName] && Boolean(formik.errors[fieldName]);
  const accentColor = "#4f46e5";

  const handleClose = () => {
    const cleaned = items.filter(v => v.trim() !== "");
    formik.setFieldValue(fieldName, cleaned.length > 0 ? cleaned : [""]);
    setOpen(false);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
        <IconComponent sx={{ fontSize: 16, color: accentColor }} />
        <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: "uppercase" }}>
          {label} *
        </Typography>
      </Box>
      <Button
        type="button"
        fullWidth
        variant="outlined"
        onClick={() => setOpen(true)}
        sx={{
          justifyContent: "flex-start", borderRadius: 2, borderColor: hasError ? "#ef4444" : "#cbd5e1",
          borderStyle: primary ? "solid" : "dashed", color: primary ? "#334155" : (hasError ? "#ef4444" : "#94a3b8"),
          textTransform: "none", py: 1, px: 1.5,
        }}
      >
        {primary ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <EditIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
              <Typography sx={{ fontSize: "0.875rem", fontWeight: 400 }}>{primary}</Typography>
            </Box>
            {extraCount > 0 ? (
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: accentColor, bgcolor: alpha(accentColor, 0.1), px: 1, py: 0.25, borderRadius: 1 }}>
                +{extraCount} more
              </Typography>
            ) : <AddIcon sx={{ fontSize: 16, color: accentColor }} />}
          </Box>
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AddIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: "0.875rem", fontWeight: 400 }}>Add {label}</Typography>
          </Box>
        )}
      </Button>
      {hasError && <Typography color="error" variant="caption">{typeof formik.errors[fieldName] === 'string' ? formik.errors[fieldName] : "Contains invalid or duplicate items"}</Typography>}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #f1f5f9" }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconComponent sx={{ fontSize: 20, color: accentColor }} /> Manage {label}
          </Typography>
          <IconButton size="small" onClick={handleClose}><Close /></IconButton>
        </Box>
        <DialogContent sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {items.map((val, idx) => {
            const hasItemError = Boolean(formik.errors[fieldName]?.[idx]);
            const errorText = formik.errors[fieldName]?.[idx] || "";
            return (
              <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <TextField
                  fullWidth size="small" type={type}
                  name={`${fieldName}[${idx}]`} value={val} onChange={formik.handleChange} onBlur={formik.handleBlur}
                  error={hasItemError} helperText={errorText}
                  placeholder={`Enter ${label.toLowerCase()}`}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
                {items.length > 1 && (
                  <IconButton size="small" color="error" sx={{ flexShrink: 0, mt: hasItemError ? -2.5 : 0 }} onClick={() => {
                    const next = [...items]; next.splice(idx, 1); formik.setFieldValue(fieldName, next);
                  }}>
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                )}
              </Box>
            );
          })}
          <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => formik.setFieldValue(fieldName, [...items, ""])} sx={{ alignSelf: "flex-start", borderRadius: 2, textTransform: "none" }}>
            Add Another
          </Button>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #f1f5f9" }}>
          <Button onClick={handleClose} variant="contained" sx={{ bgcolor: accentColor, textTransform: "none", borderRadius: 2, boxShadow: "none" }}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ManualCardModal = ({ open, onClose, onSaveSuccess, API_BASE }) => {
  const [serverError, setServerError] = useState("");

  // Validation Schema using Yup
  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required("Full Name is required"),
    email: Yup.array().of(
      Yup.string()
        .email("Enter a valid email address")
        .required("Email is required")
        .test('unique', 'This email is already added', function(value) {
          if (!value) return true;
          return this.parent.filter(v => v === value).length <= 1;
        })
    ),
    phone: Yup.array().of(
      Yup.string()
        .matches(/^[0-9+\s-]{8,20}$/, "Enter a valid phone number")
        .required("Phone number is required")
        .test('unique', 'This phone number is already added', function(value) {
          if (!value) return true;
          const d1 = value.replace(/\D/g, "").slice(-10);
          return this.parent.filter(v => {
            if (!v) return false;
            const d2 = v.replace(/\D/g, "").slice(-10);
            return d1 && d1 === d2;
          }).length <= 1;
        })
    ),
    title: Yup.string(),
    company: Yup.string(),
    website: Yup.string().url("Enter a valid URL (include https://)"),
    address: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      title: "",
      company: "",
      phone: [""],
      email: [""],
      website: "",
      address: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      setServerError("");
      try {
        const response = await axios.post(
          `${API_BASE}/save-visiting-card`,
          values,
          { withCredentials: true }
        );

        if (response.data.success) {
          resetForm();
          onSaveSuccess(); // Refresh list in parent
          onClose(); // Close modal
        }
      } catch (err) {
        setServerError(err.response?.data?.message || "Failed to save card.");
      }
    },
  });

  // Handle modal close and reset formik state
  const handleClose = () => {
    formik.resetForm();
    setServerError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: "bold", textAlign: "center" }}>
        Add Manual Visiting Card
      </DialogTitle>
      
      <form onSubmit={formik.handleSubmit}>
        <DialogContent dividers>
          {serverError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {serverError}
            </Alert>
          )}

          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Full Name *"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            <SimpleArrayField label="Emails" fieldName="email" type="email" icon={EmailIcon} formik={formik} />
            <SimpleArrayField label="Phones" fieldName="phone" type="tel" icon={PhoneIcon} formik={formik} />

            <TextField
              fullWidth
              label="Job Title"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
            />

            <TextField
              fullWidth
              label="Company Name"
              name="company"
              value={formik.values.company}
              onChange={formik.handleChange}
            />

            <TextField
              fullWidth
              label="Website (e.g., https://example.com)"
              name="website"
              value={formik.values.website}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.website && Boolean(formik.errors.website)}
              helperText={formik.touched.website && formik.errors.website}
            />

            <TextField
              fullWidth
              label="Address"
              name="address"
              multiline
              rows={2}
              value={formik.values.address}
              onChange={formik.handleChange}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={handleClose} color="inherit" disabled={formik.isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={formik.isSubmitting}
            startIcon={formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
            sx={{
              backgroundColor: "#6366F1",
              px: 3,
              "&:hover": { backgroundColor: "#4F46E5" },
            }}
          >
            {formik.isSubmitting ? "Saving..." : "Save Card"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManualCardModal;